import styled from "@emotion/styled";
import clsx from "clsx";
import type {
  FormEventHandler,
  ForwardedRef,
  PropsWithChildren,
  ReactElement,
  SyntheticEvent
} from "react";
import React, { forwardRef } from "react";
import type {
  DefaultValues,
  FieldErrors,
  FieldValues,
  UseFormReturn
} from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";

const StyledForm = styled.form`
  width: 100%;

  &.grow {
    height: 100%;
    display: flex;
  }
`;

interface Props<T extends FieldValues> {
  onSubmit?: (data: T, events: UseFormReturn<T>) => void;
  onError?: (errors: FieldErrors<T>) => void;
  onChange?: (event: SyntheticEvent, data: T, events: UseFormReturn<T>) => void;
  defaultValues?: DefaultValues<T>;
  className?: string;
  grow?: boolean;
}

function Form<T extends FieldValues>(
  props: PropsWithChildren<Props<T>>,
  ref: ForwardedRef<HTMLFormElement>
): ReactElement {
  const methods = useForm<T>({
    defaultValues: props.defaultValues
  });

  const handleChange = (event: SyntheticEvent): void => {
    props.onChange?.(event, methods.getValues(), methods);
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e): void => {
    void methods
      .handleSubmit((data) => {
        props.onSubmit?.(
          {
            ...props.defaultValues,
            ...data
          },
          methods
        );
      })(e)
      .then(() => {
        if (Object.keys(methods.formState.errors).length > 0) {
          props.onError?.(methods.formState.errors);
        }
      });
  };

  return (
    <FormProvider<T> {...methods}>
      <StyledForm
        noValidate
        ref={ref}
        className={clsx(props.className, {
          grow: props.grow
        })}
        onSubmit={handleSubmit}
        onChange={handleChange}
      >
        {props.children}
      </StyledForm>
    </FormProvider>
  );
}

export default forwardRef(Form);
